import { Injectable } from '@angular/core';
import {
  BehaviorQuestionnaires,
  TemplateModel,
  TemplateQuestionResponse,
} from '../../../../bip-template/bip-template.interface';
import { QuestionItem } from '../../../../manage-settings/manage-settings.interface';
import { DraftStepType } from '../../../insights.enums';
import { BehaviorDetailModel } from '../../../insights.interface';
import {
  CANONICAL_NATIVE_ORDER,
  LEGACY_PAGE_TO_REF,
  NATIVE_FIELD_DESCRIPTORS,
} from './define-behavior-wizard.constant';
import {
  ItemGating,
  WizardStep,
  WizardStepBuildInput,
  WizardStepKind,
} from './define-behavior-wizard.interface';

const CUSTOM_QUESTIONS_SECTION_NAME = 'Custom Questions';

// Scoped entirely to the Challenging Behavior category (behaviorQuestionaries)
// and its Custom Questions bundle — Behavior Screener, Strategy Details,
// Reports, and Questionnaires are separate template sections this service
// never reads or builds steps for.
@Injectable({
  providedIn: 'root',
})
export class DefineBehaviorWizardService {
  buildSteps(input: WizardStepBuildInput): WizardStep[] {
    const { template, templateCustomQuestions } = input;

    if (!template) {
      return this.buildDefaultSteps(templateCustomQuestions);
    }

    const sortedRows = this.sortRows(template.behaviorQuestionaries || []);
    const rowsWithHeaders = this.withSynthesizedHeaders(sortedRows);
    const selectedRows = rowsWithHeaders.filter(row => row.isSelected);
    const questionsById = new Map(
      templateCustomQuestions.map(question => [question.id, question])
    );

    const middleSteps = selectedRows
      .map(row => this.toStep(row, questionsById))
      .filter((step): step is WizardStep => step !== null);

    const bundleStep = this.buildBundleStep(
      middleSteps,
      templateCustomQuestions,
      template
    );

    return [
      this.buildCategoryStep(),
      ...middleSteps,
      ...(bundleStep ? [bundleStep] : []),
      this.buildSummaryStep(),
    ];
  }

  buildDefaultSteps(
    templateCustomQuestions: QuestionItem[] = []
  ): WizardStep[] {
    const middleSteps = CANONICAL_NATIVE_ORDER.map(lookupId =>
      this.buildNativeStep(lookupId, true, false, '')
    ).filter((step): step is WizardStep => step !== null);

    const bundleStep = this.buildBundleStepFromList(
      templateCustomQuestions.filter(question =>
        question.customQuestion?.trim()
      ),
      null
    );

    return [
      this.buildCategoryStep(),
      ...middleSteps,
      ...(bundleStep ? [bundleStep] : []),
      this.buildSummaryStep(),
    ];
  }

  resolveResumeIndex(
    steps: WizardStep[],
    detail: BehaviorDetailModel | null
  ): number {
    if (!detail) {
      return 0;
    }
    if (detail.draft === false) {
      return steps.length - 1;
    }

    const type = detail.draftStepType ?? DraftStepType.ApplicationObject;
    const ref = detail.draftStep ?? 0;

    const directIndex = this.findStepIndex(steps, type, ref);
    if (directIndex >= 0) {
      return directIndex;
    }

    if (detail.draftStepType == null && ref >= 1 && ref <= 12) {
      return this.resolveLegacyPageIndex(steps, ref);
    }

    if (type === DraftStepType.ApplicationObject && ref !== 0) {
      return this.resolveNearestNativeIndex(steps, ref);
    }

    return 0;
  }

  toDraftPosition(step: WizardStep): {
    draftStep: number;
    draftStepType: DraftStepType;
  } {
    return { draftStep: step.refId, draftStepType: step.draftStepType };
  }

  private resolveLegacyPageIndex(steps: WizardStep[], page: number): number {
    if (page === 12) {
      return steps.length - 1;
    }

    for (let candidatePage = page; candidatePage >= 1; candidatePage--) {
      const legacyRef = LEGACY_PAGE_TO_REF[candidatePage];
      if (!legacyRef) {
        continue;
      }
      const index = this.findStepIndex(
        steps,
        legacyRef.draftStepType,
        legacyRef.refId
      );
      if (index >= 0) {
        return index;
      }
    }

    return 0;
  }

  // The CustomQuestionBundle step's refId is just one of its questionIds
  // (see buildBundleStepFromList) — for that step kind, match on membership
  // in questionIds (or the legacy page-11 sentinel refId: 0) instead of
  // exact refId equality, so a resume still lands on the bundle step even if
  // its first question id shifts between saves.
  private findStepIndex(
    steps: WizardStep[],
    type: DraftStepType,
    ref: number
  ): number {
    return steps.findIndex(step => {
      if (step.draftStepType !== type) {
        return false;
      }
      if (step.kind === WizardStepKind.CustomQuestionBundle) {
        return (
          ref === 0 || step.refId === ref || step.questionIds.includes(ref)
        );
      }
      return step.refId === ref;
    });
  }

  private resolveNearestNativeIndex(
    steps: WizardStep[],
    lookupId: number
  ): number {
    const orderIndex = CANONICAL_NATIVE_ORDER.indexOf(lookupId);
    if (orderIndex === -1) {
      return 0;
    }

    for (let i = orderIndex; i >= 0; i--) {
      const candidateRef = CANONICAL_NATIVE_ORDER[i];
      const index = steps.findIndex(
        step =>
          step.draftStepType === DraftStepType.ApplicationObject &&
          step.refId === candidateRef
      );
      if (index >= 0) {
        return index;
      }
    }

    return 0;
  }

  private sortRows(rows: BehaviorQuestionnaires[]): BehaviorQuestionnaires[] {
    return [...rows].sort((a, b) => {
      if (a.behaviorTypeSequenceNumber !== b.behaviorTypeSequenceNumber) {
        return a.behaviorTypeSequenceNumber - b.behaviorTypeSequenceNumber;
      }
      return (a.questionSequenceNumber || 0) - (b.questionSequenceNumber || 0);
    });
  }

  // Ports withMissingSectionHeaders from bip-template-form.component.ts: production
  // data can omit a category's header (questionId: 0) row even when one of its
  // custom questions still references the category via applicationObjectId. Must
  // run before the isSelected filter and inherit isSelected from the triggering
  // question, or the question's step loses its place in the sequence entirely.
  private withSynthesizedHeaders(
    rows: BehaviorQuestionnaires[]
  ): BehaviorQuestionnaires[] {
    const existingHeaderLookupIds = new Set(
      rows
        .filter(row => !row.questionId)
        .map(row => row.applicationObjectId || row.sectionId)
    );

    const result: BehaviorQuestionnaires[] = [];
    for (const row of rows) {
      const lookupId = row.applicationObjectId || row.sectionId;
      if (row.questionId && !existingHeaderLookupIds.has(lookupId)) {
        result.push({
          sectionId: lookupId,
          applicationObjectId: lookupId,
          questionId: 0,
          questionSequenceNumber: 0,
          behaviorTypeSequenceNumber: row.behaviorTypeSequenceNumber,
          isSelected: row.isSelected,
          value: '',
        });
        existingHeaderLookupIds.add(lookupId);
      }
      result.push(row);
    }

    return result;
  }

  private toStep(
    row: BehaviorQuestionnaires,
    questionsById: Map<number, QuestionItem>
  ): WizardStep | null {
    const isHardStop = row.isHardStop ?? false;
    const isRequired = (row.isRequired ?? false) || isHardStop;
    const helpText = (row.helpText ?? '').trim();

    if (!row.questionId) {
      const lookupId = row.applicationObjectId || row.sectionId;
      const descriptor = NATIVE_FIELD_DESCRIPTORS[lookupId];
      if (!descriptor) {
        return null;
      }
      return {
        kind: WizardStepKind.NativeField,
        refId: lookupId,
        draftStepType: DraftStepType.ApplicationObject,
        label: descriptor.label,
        helpText,
        isRequired,
        isHardStop,
        controlNames: descriptor.controlNames,
        questionIds: [],
        questionGating: {},
      };
    }

    const question = questionsById.get(row.questionId);
    const label = question?.customQuestion || row.value || '';
    return {
      kind: WizardStepKind.EmbeddedCustomQuestion,
      refId: row.questionId,
      draftStepType: DraftStepType.Question,
      label,
      helpText,
      isRequired,
      isHardStop,
      controlNames: ['caseBehaviorCustomQuestionAnswer'],
      questionIds: [row.questionId],
      questionGating: {
        [row.questionId]: { isRequired, isHardStop, helpText },
      },
    };
  }

  private buildNativeStep(
    lookupId: number,
    isRequired: boolean,
    isHardStop: boolean,
    helpText: string
  ): WizardStep | null {
    const descriptor = NATIVE_FIELD_DESCRIPTORS[lookupId];
    if (!descriptor) {
      return null;
    }
    return {
      kind: WizardStepKind.NativeField,
      refId: lookupId,
      draftStepType: DraftStepType.ApplicationObject,
      label: descriptor.label,
      helpText,
      isRequired,
      isHardStop,
      controlNames: descriptor.controlNames,
      questionIds: [],
      questionGating: {},
    };
  }

  private buildCategoryStep(): WizardStep {
    return {
      kind: WizardStepKind.BehaviorCategory,
      // Backend requires DraftStep > 0, so this can't be the 0 sentinel used
      // elsewhere — 1 is safe here since real native lookup ids start at 146.
      refId: 1,
      draftStepType: DraftStepType.ApplicationObject,
      label: 'Behavior Category',
      helpText: '',
      isRequired: true,
      isHardStop: true,
      controlNames: ['caseId', 'behaviorCategoryId'],
      questionIds: [],
      questionGating: {},
    };
  }

  // Always the final step, appended after every native/custom-question step
  // so the wizard's existing isLastStep/canProceed footer logic naturally
  // shows Save & Close / Continue on the summary rather than on Goals or the
  // bundle. refId: 2 is a sentinel like buildCategoryStep()'s refId: 1 (the
  // backend rejects DraftStep <= 0, and real native lookup ids start at 146),
  // chosen distinct from Category's refId: 1 since both share ApplicationObject.
  private buildSummaryStep(): WizardStep {
    return {
      kind: WizardStepKind.Summary,
      refId: 2,
      draftStepType: DraftStepType.ApplicationObject,
      label: 'Summary',
      helpText: '',
      isRequired: false,
      isHardStop: false,
      controlNames: [],
      questionIds: [],
      questionGating: {},
    };
  }

  private buildBundleStep(
    middleSteps: WizardStep[],
    templateCustomQuestions: QuestionItem[],
    template: TemplateModel
  ): WizardStep | null {
    const customQuestionsSection = template.templateSections?.find(
      section => section.sectionName === CUSTOM_QUESTIONS_SECTION_NAME
    );
    if (customQuestionsSection?.isSelected === false) {
      return null;
    }

    const embeddedIds = new Set(
      middleSteps
        .filter(step => step.kind === WizardStepKind.EmbeddedCustomQuestion)
        .map(step => step.refId)
    );
    const bundleQuestions = templateCustomQuestions.filter(
      question =>
        question.customQuestion?.trim() && !embeddedIds.has(question.id)
    );

    return this.buildBundleStepFromList(bundleQuestions, template);
  }

  private buildBundleStepFromList(
    questions: QuestionItem[],
    template: TemplateModel | null
  ): WizardStep | null {
    if (questions.length === 0) {
      return null;
    }

    const templateQuestionsById = new Map<number, TemplateQuestionResponse>(
      (template?.templateQuestions || []).map(tq => [tq.questionId, tq])
    );

    const sorted = [...questions].sort((a, b) => {
      const seqA =
        templateQuestionsById.get(a.id)?.sequenceNumber ??
        a.sequenceNumber ??
        0;
      const seqB =
        templateQuestionsById.get(b.id)?.sequenceNumber ??
        b.sequenceNumber ??
        0;
      return seqA - seqB;
    });

    const questionGating: Record<number, ItemGating> = {};
    let stepIsHardStop = false;
    let stepIsRequired = false;

    const questionIds = sorted.map(question => {
      const templateSettings = templateQuestionsById.get(question.id);
      const isHardStop = templateSettings?.isHardStop ?? false;
      const isRequired =
        (templateSettings?.isRequired ?? question.isRequired ?? false) ||
        isHardStop;
      const helpText = (templateSettings?.helpText ?? '').trim();

      questionGating[question.id] = { isRequired, isHardStop, helpText };
      stepIsHardStop = stepIsHardStop || isHardStop;
      stepIsRequired = stepIsRequired || isRequired;

      return question.id;
    });

    return {
      kind: WizardStepKind.CustomQuestionBundle,
      // Backend requires DraftStep > 0, and real custom-question ids are
      // arbitrary positive integers, so 0 isn't a safe sentinel here (unlike
      // buildCategoryStep()'s fixed 1). Use the bundle's first real question
      // id instead — resolveResumeIndex() matches this step by membership in
      // questionIds, not refId equality, so this stays resolvable even if
      // that first id's position shifts between saves.
      refId: questionIds[0],
      draftStepType: DraftStepType.Question,
      label: 'Custom Questions',
      helpText: '',
      isRequired: stepIsRequired,
      isHardStop: stepIsHardStop,
      controlNames: ['caseBehaviorCustomQuestionAnswer'],
      questionIds,
      questionGating,
    };
  }
}
